/**
 * SitePreview Component
 *
 * Main component for rendering a site preview with edit mode support.
 * Renders all sections from the content model.
 */

import { useMemo, useState, useCallback } from 'react';
import { RendererProvider, StagedChange } from '../context';
import {
  EditableSiteContent,
  EditablePage,
  EditableSection,
  SectionConfig,
  RenderContext,
} from '../types';
import { getSection, PlaceholderSection } from '../sections';
import { EditModal } from './EditModal';

// =============================================================================
// TYPES
// =============================================================================

export interface ColorOverrides {
  primary?: string;
  secondary?: string;
}

interface SitePreviewProps {
  content: EditableSiteContent;
  pageSlug?: string;
  editMode?: boolean;
  initialChanges?: Record<string, StagedChange>;
  onChangeStaged?: (changes: Record<string, StagedChange>) => void;
  onPageChange?: (pageSlug: string) => void;
  className?: string;
  /** Show the editor toolbar with page tabs */
  showToolbar?: boolean;
  /** Optional color overrides for live preview */
  colorOverrides?: ColorOverrides;
}

// =============================================================================
// SECTION RENDERER
// =============================================================================

interface SectionRendererProps {
  section: EditableSection;
  ctx: RenderContext;
}

function SectionRenderer({ section, ctx }: SectionRendererProps) {
  const SectionComponent = getSection(section.type);

  if (!SectionComponent) {
    return <PlaceholderSection type={section.type} />;
  }

  // Build section data from elements
  const sectionData = buildSectionData(section);

  return (
    <SectionComponent
      data={sectionData}
      ctx={ctx}
      sectionId={section.id}
      elements={section.elements}
    />
  );
}

// =============================================================================
// HELPER: Build section data from editable elements
// =============================================================================

function buildSectionData(section: EditableSection): SectionConfig['data'] {
  const data: Record<string, unknown> = {
    variant: section.variant,
  };

  // Convert elements back to nested data structure
  for (const element of section.elements) {
    setNestedValue(data, element.path, element.value);
  }

  return data as SectionConfig['data'];
}

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];
    const isArrayIndex = /^\d+$/.test(nextPart);

    if (!(part in current)) {
      current[part] = isArrayIndex ? [] : {};
    }
    current = current[part] as Record<string, unknown>;
  }

  const lastPart = parts[parts.length - 1];
  current[lastPart] = value;
}

// =============================================================================
// PAGE RENDERER
// =============================================================================

interface PageRendererProps {
  page: EditablePage;
  ctx: RenderContext;
}

function PageRenderer({ page, ctx }: PageRendererProps) {
  return (
    <>
      {page.sections.map((section) => (
        <SectionRenderer key={section.id} section={section} ctx={ctx} />
      ))}
    </>
  );
}

// =============================================================================
// EDITOR TOOLBAR
// =============================================================================

interface EditorToolbarProps {
  content: EditableSiteContent;
  currentPage: string;
  onPageChange: (pageSlug: string) => void;
}

function EditorToolbar({ content, currentPage, onPageChange }: EditorToolbarProps) {
  const allPages = content.navigation.pages;

  // Get a clean label for the page tab (just the page name, not the full title)
  const getPageTabLabel = (page: { slug: string; label: string }) => {
    // If label contains " | ", take the first part (e.g., "Services | Sammy's Soups" -> "Services")
    if (page.label.includes(' | ')) {
      return page.label.split(' | ')[0];
    }
    // If it's the home page with a long label, just show "Home"
    if (page.slug === 'home' && page.label.length > 10) {
      return 'Home';
    }
    // Capitalize the slug as fallback if label is too long
    if (page.label.length > 20) {
      return page.slug.charAt(0).toUpperCase() + page.slug.slice(1);
    }
    return page.label;
  };

  return (
    <div className="sticky top-0 z-[60] bg-gray-900 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Page Tabs */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400 mr-3 uppercase tracking-wide">Editing:</span>
            {allPages.map((page) => (
              <button
                key={page.slug}
                onClick={() => onPageChange(page.slug)}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  page.slug === currentPage
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {getPageTabLabel(page)}
                {!page.visible && (
                  <span className="ml-1 text-xs text-gray-500">(hidden)</span>
                )}
              </button>
            ))}
          </div>

          {/* Edit mode indicator */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-600 text-white rounded">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Mode
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// NAVIGATION
// =============================================================================

interface NavigationProps {
  content: EditableSiteContent;
  currentPage: string;
  ctx: RenderContext;
  onPageChange?: (pageSlug: string) => void;
}

function Navigation({ content, currentPage, ctx, onPageChange }: NavigationProps) {
  const { recipe } = ctx;
  const visiblePages = content.navigation.pages.filter(p => p.visible);

  const handleNavClick = (e: React.MouseEvent, pageSlug: string) => {
    if (onPageChange) {
      e.preventDefault();
      onPageChange(pageSlug);
    }
    // If no onPageChange, let the link navigate normally
  };

  // Get a clean navigation label (strip business name from meta title)
  const getNavLabel = (page: { slug: string; label: string }) => {
    // If label contains " | ", take the first part (e.g., "Services | Sammy's Soups" -> "Services")
    if (page.label.includes(' | ')) {
      return page.label.split(' | ')[0];
    }
    // For home page with long labels, just show "Home"
    if (page.slug === 'home' && page.label.length > 20) {
      return 'Home';
    }
    // If label is too long, use capitalized slug
    if (page.label.length > 25) {
      return page.slug.charAt(0).toUpperCase() + page.slug.slice(1);
    }
    return page.label;
  };

  return (
    <nav
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm"
      style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Business Name */}
          <a
            href="/"
            onClick={(e) => handleNavClick(e, 'home')}
            className="font-bold text-xl"
            style={{ color: recipe.colors.primary }}
          >
            {content.businessName}
          </a>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {visiblePages.map((page) => (
              <a
                key={page.slug}
                href={`/${page.slug === 'home' ? '' : page.slug}`}
                onClick={(e) => handleNavClick(e, page.slug)}
                className={`text-sm font-medium transition-colors ${
                  page.slug === currentPage
                    ? ''
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={page.slug === currentPage ? { color: recipe.colors.primary } : undefined}
              >
                {getNavLabel(page)}
              </a>
            ))}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2 text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
}

// =============================================================================
// FOOTER
// =============================================================================

interface FooterProps {
  content: EditableSiteContent;
  ctx: RenderContext;
}

function Footer({ content, ctx }: FooterProps) {
  const { recipe, currentYear } = ctx;
  const isDark = recipe.mode === 'dark';

  return (
    <footer
      className={`py-12 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}
      style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Business Info */}
          <div className="text-center md:text-left">
            <p className="font-semibold">{content.footer.businessName}</p>
            {content.footer.tagline && (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {content.footer.tagline}
              </p>
            )}
          </div>

          {/* Social Links */}
          {content.footer.social && Object.keys(content.footer.social).length > 0 && (
            <div className="flex items-center gap-4">
              {Object.entries(content.footer.social).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
                >
                  <span className="sr-only">{platform}</span>
                  <SocialIcon platform={platform} />
                </a>
              ))}
            </div>
          )}

          {/* Copyright */}
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            © {currentYear} {content.footer.businessName}. All rights reserved.
          </p>
        </div>

        {/* Powered by niftee */}
        <div className="mt-8 pt-8 border-t border-gray-200 text-center">
          <a
            href="https://niftee.ai"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`}
          >
            Powered by niftee®
          </a>
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// SOCIAL ICON
// =============================================================================

function SocialIcon({ platform }: { platform: string }) {
  const icons: Record<string, JSX.Element> = {
    facebook: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    instagram: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
      </svg>
    ),
    twitter: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
      </svg>
    ),
    linkedin: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  };

  return icons[platform] || null;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function SitePreview({
  content,
  pageSlug: controlledPageSlug,
  editMode = false,
  initialChanges = {},
  onChangeStaged,
  onPageChange,
  className = '',
  showToolbar = false,
  colorOverrides,
}: SitePreviewProps) {
  // Internal state for uncontrolled mode
  const [internalPageSlug, setInternalPageSlug] = useState(controlledPageSlug ?? 'home');

  // Use controlled or uncontrolled page slug
  const currentPageSlug = controlledPageSlug ?? internalPageSlug;

  // Handle page change - call external handler or update internal state
  const handlePageChange = useCallback((newSlug: string) => {
    if (onPageChange) {
      onPageChange(newSlug);
    } else {
      setInternalPageSlug(newSlug);
    }
  }, [onPageChange]);

  // Get the current page
  const page = content.pages[currentPageSlug];

  // Build render context with optional color overrides
  const ctx: RenderContext = useMemo(() => {
    let recipe = content.recipe;

    // Apply color overrides if provided
    if (colorOverrides?.primary || colorOverrides?.secondary) {
      recipe = {
        ...recipe,
        colors: {
          ...recipe.colors,
          ...(colorOverrides.primary && { primary: colorOverrides.primary }),
          ...(colorOverrides.secondary && { secondary: colorOverrides.secondary }),
        },
      };
    }

    return {
      recipe,
      websiteId: content.websiteId,
      businessName: content.businessName,
      currentYear: new Date().getFullYear().toString(),
      editMode,
    };
  }, [content, editMode, colorOverrides]);

  if (!page) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Page not found: {currentPageSlug}</p>
        <div className="mt-4">
          <p className="text-sm text-gray-400">Available pages:</p>
          <div className="flex gap-2 justify-center mt-2">
            {Object.keys(content.pages).map((slug) => (
              <button
                key={slug}
                onClick={() => handlePageChange(slug)}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                {slug}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <RendererProvider
      content={content}
      initialEditMode={editMode}
      initialChanges={initialChanges}
      onChangeStaged={onChangeStaged}
    >
      <div className={`site-preview ${className}`}>
        {/* Editor Toolbar (only in edit mode with showToolbar) */}
        {editMode && showToolbar && (
          <EditorToolbar
            content={content}
            currentPage={currentPageSlug}
            onPageChange={handlePageChange}
          />
        )}

        {/* Navigation */}
        <Navigation
          content={content}
          currentPage={currentPageSlug}
          ctx={ctx}
          onPageChange={editMode ? handlePageChange : undefined}
        />

        {/* Page Content */}
        <main>
          <PageRenderer page={page} ctx={ctx} />
        </main>

        {/* Footer */}
        <Footer content={content} ctx={ctx} />

        {/* Edit Modal (appears when element is selected in edit mode) */}
        {editMode && <EditModal />}
      </div>
    </RendererProvider>
  );
}

export default SitePreview;
